import React, { useState, useEffect, useMemo } from 'react'
import { createComponent } from '@ycos/fela'
import { USERINFO_STATUS } from '@ycos/constants'
import LogInComponent from './LogInComponent'
import OptInComponent from './OptInComponent'

const CreateAccountOptInWrapper = createComponent('CreateAccountOptInWrapper', ({ brand }) => ({
  direction: 'ltr',
  maxWidth: '676px',
  width: brand === 'theoutnet' ? '100%' : undefined,
}))

export const useTierInfo = () => {
  const [tierInfo, setTierInfo] = useState({
    userTierInfo: null,
    userTierInfoStatus: USERINFO_STATUS.NOT_LOADED
  })

  useEffect(() => {
    const fetchTierInfo = async () => {
      try {
        setTierInfo((prev) => ({
          ...prev,
          userTierInfoStatus: USERINFO_STATUS.LOADING
        }))

        const result = await window.SF?.getTierInfo?.()

        setTierInfo({
          userTierInfo: result || null, // Ensure null if undefined
          userTierInfoStatus: USERINFO_STATUS.LOADED
        })
      } catch (error) {
        console.error('Error fetching tier info:', error)
        setTierInfo({
          userTierInfo: null,
          userTierInfoStatus: USERINFO_STATUS.LOADED
        })
      }
    }

    fetchTierInfo()
  }, [])

  return tierInfo
}

const LoyaltyCreateAccountOptIn = ({
  item,
  locale,
  brand,
  forStoryBookUseOnly = {
    showOptInComponent: false,
    showLoginComponent: false
  },
  onSignInButtonClick = () => { },
  onCreateAccountButtonClick = () => { },
  onOptInButtonClick = () => { }
}) => {
  const { userTierInfo, userTierInfoStatus } = useTierInfo()
  const [isUserSummaryNull, setIsUserSummaryNull] = useState(false) // only matters for theoutnet
  const [isUserSummaryLoading, setIsUserSummaryLoading] = useState(brand?.brandId === 'theoutnet') // only matters for theoutnet
  // only matters for theoutnet
  useEffect(() => {
    const checkUserSummary = async () => {
      if (brand?.brandId === 'theoutnet' && window.SF?.getUserSummary) {
        setIsUserSummaryLoading(true)
        try {
          const result = await window.SF.getUserSummary()
          setIsUserSummaryNull(result == null)
        } catch (err) {
          console.error('Error fetching user summary:', err)
          setIsUserSummaryNull(true)
        }
        setIsUserSummaryLoading(false)
      }
    }

    checkUserSummary()
  }, [brand])

  // Memoize computed values for better performance
  const { loggedIn, isOptedIn } = useMemo(() => {
    const loggedIn = userTierInfoStatus === USERINFO_STATUS.LOADED && userTierInfo
    const isOptedIn = loggedIn && userTierInfo?.loyaltyConsent

    return { loggedIn, isOptedIn }
  }, [userTierInfo, userTierInfoStatus])

  // Determine what to render
  const renderContent = useMemo(() => {
    if (userTierInfoStatus === USERINFO_STATUS.LOADING || isUserSummaryLoading) {
      return null
    }

    // Storybook override takes precedence
    if (forStoryBookUseOnly.showLoginComponent) {
      return (
        <LogInComponent
          brand={brand?.brandId}
          item={item.items[0]}
          locale={locale}
          onSignInButtonClick={onSignInButtonClick}
          onCreateAccountButtonClick={onCreateAccountButtonClick}
        />
      )
    }

    if (forStoryBookUseOnly.showOptInComponent) {
      return <OptInComponent brand={brand?.brandId} item={item.items[1]} locale={locale} onOptInButtonClick={onOptInButtonClick} />
    }

    // only matters for theoutnet
    if (brand?.brandId === 'theoutnet' && isUserSummaryNull) {
      return (
        <LogInComponent
          brand={brand?.brandId}
          item={item.items[0]}
          locale={locale}
          onSignInButtonClick={onSignInButtonClick}
          onCreateAccountButtonClick={onCreateAccountButtonClick}
        />
      )
    }

    // Normal flow
    if (['mrp', 'nap'].includes(brand?.brandId)) {
      if (!loggedIn) {
        return (
          <LogInComponent
            brand={brand?.brandId}
            item={item.items[0]}
            locale={locale}
            onSignInButtonClick={onSignInButtonClick}
            onCreateAccountButtonClick={onCreateAccountButtonClick}
          />
        )
      }

      if (!isOptedIn) {
        return <OptInComponent brand={brand?.brandId} item={item.items[1]} locale={locale} onOptInButtonClick={onOptInButtonClick} />
      }
    }

    return null
  }, [loggedIn, isOptedIn, userTierInfoStatus, forStoryBookUseOnly, brand, item, locale, onSignInButtonClick, onCreateAccountButtonClick, onOptInButtonClick])

  if (!renderContent) {
    return null
  }

  return <CreateAccountOptInWrapper data-testid="LoyaltyCreateAccountOptIn" brand={brand?.brandId}>{renderContent}</CreateAccountOptInWrapper>
}

LoyaltyCreateAccountOptIn.layoutVariant = 'isc-loyalty-create-account-opt-in'

export default LoyaltyCreateAccountOptIn